// src/pages/ProductDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, 
  ShoppingBag, 
  Share2, 
  ZoomIn, 
  Star,
  Minus,
  Plus,
  ArrowLeft,
  Eye,
  Clock,
  Truck,
  Shield,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

// Header is now in PublicLayout
import { ProductService } from '../features/october/services/productService';
import { useCart } from '../features/october/hooks/useCart';
import { useWishlist } from '../features/october/hooks/useCart';

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Hooks
  const { addToCart, isInCart, getItemQuantity } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch product data
  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch main product
        const productData = await ProductService.getProductById(productId);
        setProduct(productData);
        
        // Fetch related products
        const related = await ProductService.getRelatedProducts(productId, 4);
        setRelatedProducts(related);
        
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(err.message || 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProductData();
    }
  }, [productId]);

  // Enhanced image gallery - responsive, no cropping
  const getProductImages = () => {
    if (!product) return [];
    
    // If product has multiple images
    if (product.images && Array.isArray(product.images)) {
      return product.images;
    }
    
    // Fallback to main image repeated (for demo)
    return [product.image, product.image, product.image, product.image];
  };

  // Actions
  const handleAddToCart = () => {
    addToCart(product, quantity);
  };

  const handleWishlistToggle = () => {
    toggleWishlist(product);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: product.description,
          url: window.location.href,
        });
      } catch (err) {
        setShowShareModal(true);
      }
    } else {
      setShowShareModal(true);
    }
  };

  const handleImageNavigation = (direction) => {
    const images = getProductImages();
    if (direction === 'next') {
      setSelectedImageIndex((prev) => (prev + 1) % images.length);
    } else {
      setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  // Dark luxury loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark">
        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid lg:grid-cols-2 gap-8"
            >
              {/* Image skeleton */}
              <div className="space-y-4">
                <div className="aspect-square bg-brand-charcoal/50 backdrop-blur-md rounded-3xl animate-pulse border border-brand-gold/20" />
                <div className="grid grid-cols-4 gap-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="aspect-square bg-brand-charcoal/50 backdrop-blur-md rounded-2xl animate-pulse border border-brand-gold/20" />
                  ))}
                </div>
              </div>
              
              {/* Content skeleton */}
              <div className="space-y-6">
                <div className="h-8 bg-brand-charcoal/50 backdrop-blur-md rounded-2xl animate-pulse border border-brand-gold/20" />
                <div className="h-12 bg-brand-charcoal/50 backdrop-blur-md rounded-2xl animate-pulse border border-brand-gold/20" />
                <div className="h-24 bg-brand-charcoal/50 backdrop-blur-md rounded-2xl animate-pulse border border-brand-gold/20" />
                <div className="h-32 bg-brand-charcoal/50 backdrop-blur-md rounded-2xl animate-pulse border border-brand-gold/20" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Enhanced error state
  if (error || !product) {
    return (
      <div className="min-h-screen bg-brand-dark">
        <div className="pt-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto px-4 py-16 text-center"
          >
            <div className="w-20 h-20 bg-brand-charcoal/50 backdrop-blur-md rounded-3xl flex items-center justify-center mx-auto mb-6 border border-brand-gold/20">
              <Eye className="w-10 h-10 text-white/40" />
            </div>
            <h1 className="text-2xl font-heading font-normal text-white mb-4 tracking-wide">Product Not Found</h1>
            <p className="text-white/70 font-body font-light mb-8 leading-relaxed">
              {error || 'The product you\'re looking for doesn\'t exist or has been removed.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-body font-medium transition-colors touch-manipulation"
              >
                <ArrowLeft size={20} />
                Go Back
              </button>
              <Link
                to="/kganya-scents"
                className="flex items-center justify-center gap-2 px-6 py-3 bg-brand-charcoal hover:bg-brand-charcoal-light text-white rounded-xl font-body font-medium transition-colors border border-brand-gold/20 touch-manipulation"
              >
                View All Products
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  const images = getProductImages();
  const inCart = isInCart(product.id);
  const cartQuantity = getItemQuantity(product.id);
  const inWishlist = isInWishlist(product.id);

  return (
    <div className="min-h-screen bg-pontso-black">
      
      {/* Enhanced mobile back button */}
      {isMobile && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="fixed top-20 left-4 z-40"
        >
          <button
            onClick={() => navigate(-1)}
            className="p-3 bg-black/90 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 text-white"
          >
            <ArrowLeft size={20} strokeWidth={1.5} />
          </button>
        </motion.div>
      )}

      {/* Dark luxury breadcrumb - Desktop only */}
      {!isMobile && (
        <div className="pt-24 pb-4 bg-pontso-black backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-4">
            <motion.nav
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center space-x-2 text-sm font-light"
            >
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                Home
              </Link>
              <span className="text-gray-600">/</span>
              <Link to="/kganya-scents" className="text-gray-400 hover:text-white transition-colors">
                Kganya Scents
              </Link>
              <span className="text-gray-600">/</span>
              <span className="text-white truncate">{product.name}</span>
            </motion.nav>
          </div>
        </div>
      )}

      {/* Main Content - Dark Luxury */}
      <div className={`${isMobile ? 'pt-20' : 'pt-8'} pb-16 bg-pontso-black backdrop-blur-xl`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            
            {/* Enhanced Image Gallery - No Cropping, Responsive */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              {/* Main Image - Responsive, No Cropping */}
              <div className="relative group">
                <div className="bg-pontso-charcoal backdrop-blur-sm rounded-2xl overflow-hidden relative border border-pontso-gold/20">
                  <motion.img
                    key={selectedImageIndex}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    src={images[selectedImageIndex]}
                    alt={`${product.name} - View ${selectedImageIndex + 1}`}
                    className="w-full h-auto object-contain cursor-zoom-in p-6 min-h-[400px] max-h-[600px]"
                    onClick={() => setIsImageZoomed(true)}
                    style={{ aspectRatio: 'auto' }}
                  />
                  
                  {/* Enhanced image navigation */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={() => handleImageNavigation('prev')}
                        className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/70 backdrop-blur-md text-white rounded-2xl opacity-0 group-hover:opacity-100 transition-all hover:bg-black/90 border border-white/20"
                      >
                        <ChevronLeft size={20} strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => handleImageNavigation('next')}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/70 backdrop-blur-md text-white rounded-2xl opacity-0 group-hover:opacity-100 transition-all hover:bg-black/90 border border-white/20"
                      >
                        <ChevronRight size={20} strokeWidth={1.5} />
                      </button>
                    </>
                  )}
                  
                  {/* Enhanced zoom icon */}
                  <div className="absolute top-4 right-4 p-3 bg-black/70 backdrop-blur-md text-white rounded-2xl opacity-0 group-hover:opacity-100 transition-all border border-white/20">
                    <ZoomIn size={16} strokeWidth={1.5} />
                  </div>
                </div>
              </div>
              
              {/* Enhanced Thumbnail Gallery */}
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                  {images.map((img, index) => (
                    <motion.button
                      key={index}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`bg-pontso-charcoal backdrop-blur-sm rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                        selectedImageIndex === index ? 'border-pontso-gold' : 'border-pontso-gold/20 hover:border-pontso-gold/40'
                      }`}
                    >
                      <img 
                        src={img} 
                        alt={`${product.name} thumbnail ${index + 1}`} 
                        className="w-full h-auto object-contain p-2 min-h-[80px]" 
                      />
                    </motion.button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Enhanced Product Info - Dark Luxury */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              {/* Header with Brand Colors */}
              <div>
                {/* Category & Type */}
                <div className="flex items-center gap-2 mb-3">
                  <span                     className={`px-3 py-1.5 rounded-xl text-xs font-body font-light tracking-wide ${
                    product.category === 'male' ? 'bg-pontso-gold/20 text-pontso-gold border border-pontso-gold/30' :
                    product.category === 'female' ? 'bg-pontso-gold/20 text-pontso-gold border border-pontso-gold/30' :
                    'bg-pontso-charcoal text-white/60 border border-pontso-gold/30'
                  }`}>
                    {product.category === 'male' ? 'For Him' : 
                     product.category === 'female' ? 'For Her' : 'Unisex'}
                  </span>
                  <span className="text-sm text-gray-400 font-light">{product.type}</span>
                </div>
                
                {/* Product Name */}
                <h1 className="text-3xl lg:text-4xl font-heading font-normal text-white mb-4 tracking-wide">
                  {product.name}
                </h1>
                
                {/* Rating */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        size={16}
                        className={`${i < Math.floor(product.rating || 0) ? 'text-pontso-gold fill-current' : 'text-pontso-charcoal'}`}
                      />
                    ))}
                    <span className="ml-2 text-sm text-gray-400 font-light">
                      {product.rating || 0} ({product.reviews || 0} reviews)
                    </span>
                  </div>
                </div>
              </div>

              {/* Price */}
              <div className="border-y border-white/10 py-6">
                <div className="text-3xl font-light text-white mb-1">
                  {product.price}
                </div>
                <p className="text-sm text-gray-400 font-light">Price includes VAT • Free shipping over R500</p>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-lg font-light text-white mb-3 tracking-wide">About this fragrance</h3>
                <p className="text-gray-400 leading-relaxed font-light">{product.description}</p>
              </div>

              {/* Fragrance Notes */}
              {product.notes && (
                <div>
                  <h3 className="text-lg font-light text-white mb-4 tracking-wide">Fragrance Profile</h3>
                  <div className="space-y-3">
                    {product.notes.top && (
                      <div className="flex">
                        <span className="text-sm font-light text-white w-20">Top:</span>
                        <span className="text-sm text-gray-400 font-light">{product.notes.top.join(', ')}</span>
                      </div>
                    )}
                    {product.notes.heart && (
                      <div className="flex">
                        <span className="text-sm font-light text-white w-20">Heart:</span>
                        <span className="text-sm text-gray-400 font-light">{product.notes.heart.join(', ')}</span>
                      </div>
                    )}
                    {product.notes.base && (
                      <div className="flex">
                        <span className="text-sm font-light text-white w-20">Base:</span>
                        <span className="text-sm text-gray-400 font-light">{product.notes.base.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Product Details */}
              <div className="grid grid-cols-2 gap-4 py-6 border-y border-white/10">
                {product.intensity && (
                  <div>
                    <span className="text-sm font-light text-white">Intensity:</span>
                    <span className="text-sm text-gray-400 font-light ml-2">{product.intensity}</span>
                  </div>
                )}
                {product.longevity && (
                  <div>
                    <span className="text-sm font-light text-white">Longevity:</span>
                    <span className="text-sm text-gray-400 font-light ml-2">{product.longevity}</span>
                  </div>
                )}
                {product.sillage && (
                  <div>
                    <span className="text-sm font-light text-white">Sillage:</span>
                    <span className="text-sm text-gray-400 font-light ml-2">{product.sillage}</span>
                  </div>
                )}
                {product.season && (
                  <div>
                    <span className="text-sm font-light text-white">Season:</span>
                    <span className="text-sm text-gray-400 font-light ml-2">{product.season}</span>
                  </div>
                )}
              </div>

              {/* Quantity & Actions */}
              <div className="space-y-6">
                {/* Enhanced Quantity Selector */}
                <div>
                  <label className="text-sm font-light text-white mb-3 block tracking-wide">Quantity</label>
                  <div className="flex items-center bg-pontso-charcoal backdrop-blur-md border border-pontso-gold/20 rounded-xl w-fit">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-3 hover:bg-white/10 transition-colors rounded-l-2xl text-gray-400 hover:text-white"
                    >
                      <Minus size={16} strokeWidth={1.5} />
                    </button>
                    <span className="px-6 py-3 border-x border-white/20 font-light min-w-[60px] text-center text-white">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="p-3 hover:bg-white/10 transition-colors rounded-r-2xl text-gray-400 hover:text-white"
                    >
                      <Plus size={16} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>

                {/* Enhanced Action Buttons with Brand Colors */}
                <div className="flex gap-4">
                  {inCart ? (
                    <div className="flex-1 flex items-center justify-center gap-2 bg-red-500/20 text-red-400 py-4 px-6 rounded-2xl font-light border border-red-500/30 backdrop-blur-md">
                      <ShoppingBag size={20} strokeWidth={1.5} />
                      <span>In Cart ({cartQuantity})</span>
                    </div>
                  ) : (
                    <button
                      onClick={handleAddToCart}
                      disabled={!product.inStock}
                      className={`flex-1 flex items-center justify-center gap-2 py-4 px-6 rounded-xl font-body font-medium transition-all duration-200 ${
                        product.inStock
                          ? 'bg-pontso-gold hover:bg-pontso-gold-light text-pontso-black shadow-lg'
                          : 'bg-pontso-charcoal text-white/40 cursor-not-allowed'
                      }`}
                    >
                      <ShoppingBag size={20} strokeWidth={1.5} />
                      <span>{product.inStock ? 'Add to Cart' : 'Out of Stock'}</span>
                    </button>
                  )}

                  {/* Enhanced Wishlist */}
                  <button
                    onClick={handleWishlistToggle}
                    className={`p-4 rounded-2xl border transition-all duration-200 backdrop-blur-md ${
                      inWishlist 
                        ? 'bg-red-500/20 border-red-500/30 text-red-400' 
                        : 'border-white/20 text-gray-400 hover:border-white/40 hover:text-white'
                    }`}
                  >
                    <Heart size={20} fill={inWishlist ? "currentColor" : "none"} strokeWidth={1.5} />
                  </button>

                  {/* Enhanced Share */}
                  <button
                    onClick={handleShare}
                    className="p-4 border border-white/20 text-gray-400 hover:border-white/40 hover:text-white rounded-2xl transition-all duration-200 backdrop-blur-md"
                  >
                    <Share2 size={20} strokeWidth={1.5} />
                  </button>
                </div>

                {/* Enhanced trust badges */}
                <div className="flex items-center gap-6 text-sm text-gray-400 pt-4 font-light">
                  <div className="flex items-center gap-2">
                    <Truck size={16} strokeWidth={1.5} />
                    <span>Free shipping</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield size={16} strokeWidth={1.5} />
                    <span>Secure payment</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={16} strokeWidth={1.5} />
                    <span>Fast delivery</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Enhanced Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 bg-black/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-4">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl lg:text-3xl font-light text-white mb-8 text-center tracking-wide"
            >
              You might also love
            </motion.h2>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct, index) => (
                <motion.div
                  key={relatedProduct.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/kganya-scents/product/${relatedProduct.id}`}
                    className="group block bg-black/90 backdrop-blur-xl rounded-3xl border border-white/10 hover:shadow-2xl transition-all duration-300 overflow-hidden"
                  >
                    <div className="overflow-hidden bg-white/5">
                      <img
                        src={relatedProduct.image}
                        alt={relatedProduct.name}
                        className="w-full h-auto object-contain p-4 group-hover:scale-105 transition-transform duration-500 min-h-[200px]"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-light text-white mb-2 group-hover:text-gray-300 transition-colors line-clamp-2 tracking-wide">
                        {relatedProduct.name}
                      </h3>
                      <div className="flex items-center justify-between">
                        <span className="font-light text-white">{relatedProduct.price}</span>
                        <span className={`text-xs px-2 py-1 rounded-lg font-light ${
                          relatedProduct.category === 'male' ? 'bg-yellow-500/20 text-yellow-400' :
                          relatedProduct.category === 'female' ? 'bg-red-500/20 text-red-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {relatedProduct.category === 'male' ? 'Him' : 
                           relatedProduct.category === 'female' ? 'Her' : 'All'}
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Enhanced Image Zoom Modal */}
      <AnimatePresence>
        {isImageZoomed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setIsImageZoomed(false)}
          >
            <motion.img
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              src={images[selectedImageIndex]}
              alt={product.name}
              className="max-w-full max-h-full object-contain"
            />
            <button
              onClick={() => setIsImageZoomed(false)}
              className="absolute top-4 right-4 p-3 bg-black/70 backdrop-blur-md text-white rounded-2xl hover:bg-black/90 transition-colors border border-white/20"
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Enhanced Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
            onClick={() => setShowShareModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-black/90 backdrop-blur-xl rounded-3xl p-6 max-w-sm w-full border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-light text-white mb-4 tracking-wide">Share this product</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigator.clipboard.writeText(window.location.href)}
                  className="w-full p-3 bg-white/10 hover:bg-white/20 rounded-2xl text-left transition-colors font-light text-white border border-white/20"
                >
                  Copy link
                </button>
                <button className="w-full p-3 bg-white/10 hover:bg-white/20 rounded-2xl text-left transition-colors font-light text-white border border-white/20">
                  Share on WhatsApp
                </button>
                <button className="w-full p-3 bg-white/10 hover:bg-white/20 rounded-2xl text-left transition-colors font-light text-white border border-white/20">
                  Share on Facebook
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductDetail;